import { createServerFn } from "@tanstack/solid-start";
import { z } from "zod";
import { withSentrySpan } from "~/lib/sentry-span";
import { authFunctionMiddleware } from "~/server/middleware/auth";

export const GetPnLPageDataInputSchema = z.object({
  businessId: z.string().trim().min(1),
});

/**
 * Aggregate server function for the P&L report page.
 *
 * Fetches the default P&L report and business detail (for the class filter) in
 * a single HTTP round trip on client-side navigation. The loader then seeds the
 * individual TanStack Query caches via setQueryData so the component renders
 * with data on the first paint (including SSR on a hard refresh).
 *
 * During SSR this runs as a direct function call — no HTTP involved either way.
 */
export const getPnLPageData = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => GetPnLPageDataInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("reports.pnl.pageData", async () => {
      const [businessMod, pnlMod] = await Promise.all([
        import("../business/business.server"),
        import("./p-and-l.server"),
      ]);
      const { getBusinessDetailForUser } = businessMod;
      const { getPnLForUser } = pnlMod;

      const [businessDetail, pnl] = await Promise.all([
        getBusinessDetailForUser(context.user.id, data.businessId),
        // Default P&L: year-to-date, all classes. Client-side filter changes
        // refetch via the resource.
        getPnLForUser(context.user.id, data.businessId),
      ]);

      return { businessDetail, pnl } as const;
    });
  });
